import React, { useState, useEffect, useRef } from 'react';
import { Play, X } from 'lucide-react';

const TAPE_COLORS = [
  'rgba(210,190,130,0.45)',
  'rgba(170,205,185,0.42)',
  'rgba(205,175,175,0.42)',
  'rgba(175,185,215,0.40)',
];

const getTapeStyle = (rotation, position = 'top-center') => {
  const idx = Math.abs(Math.round((rotation || 0) * 10)) % TAPE_COLORS.length;
  const color = TAPE_COLORS[idx];
  const base = {
    position: 'absolute',
    width: '68px',
    height: '17px',
    background: color,
    borderRadius: '2px',
    zIndex: 2,
    pointerEvents: 'none',
  };
  if (position === 'top-left') return { ...base, top: '-8px', left: '18px', transform: 'rotate(-2.8deg)' };
  if (position === 'top-right') return { ...base, top: '-8px', right: '18px', transform: 'rotate(2.8deg)' };
  return { ...base, top: '-8px', left: '50%', transform: 'translateX(-50%) rotate(-0.5deg)' };
};

const fmtDuracao = (s) => {
  if (!s) return null;
  const m = Math.floor(s / 60);
  const seg = Math.round(s % 60);
  return m > 0 ? `${m}:${String(seg).padStart(2, '0')}` : `0:${String(seg).padStart(2, '0')}`;
};

const ProjectCard = ({
  title,
  client,
  description,
  role,
  tags = [],
  poster,
  video,
  preview,
  duration,
  orientation = 'vertical',
  credits,
  year,
  rotation = 0,
  tapePosition = 'top-center',
}) => {
  const [hovered, setHovered] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const previewRef = useRef(null);

  // A miniatura é sempre cortada em 3/4, independente do formato do vídeo, para
  // que a grade fique alinhada mesmo misturando vertical e horizontal. O vídeo
  // aparece inteiro, na proporção real, só no modal.
  const modalAspect = orientation === 'vertical' ? '9/16' : '16/9';

  // Preview mudo em loop no hover (só em telas com mouse de verdade).
  useEffect(() => {
    const el = previewRef.current;
    if (!el || !preview) return;
    if (hovered) {
      el.currentTime = 0;
      const p = el.play();
      if (p && p.catch) p.catch(() => {});
    } else {
      el.pause();
    }
  }, [hovered, preview]);

  // Esc fecha o modal e o scroll da página trava enquanto ele está aberto.
  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => e.key === 'Escape' && setModalOpen(false);
    const anterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = anterior;
      window.removeEventListener('keydown', onKey);
    };
  }, [modalOpen]);

  return (
    <>
      <div
        style={{
          background: '#ffffff',
          padding: '10px 10px 20px 10px',
          boxShadow: hovered
            ? '0 14px 44px rgba(0,0,0,0.15), 0 2px 10px rgba(0,0,0,0.08)'
            : '0 4px 20px rgba(0,0,0,0.09), 0 1px 4px rgba(0,0,0,0.05)',
          transform: `rotate(${rotation}deg) translateY(${hovered ? -6 : 0}px)`,
          transition: 'box-shadow 0.3s ease, transform 0.3s ease',
          position: 'relative',
          cursor: 'pointer',
          borderRadius: '2px',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => setModalOpen(true)}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setModalOpen(true)}
        role="button"
        tabIndex={0}
        aria-label={`Assistir ${title}`}
      >
        {/* Fita adesiva */}
        <div style={getTapeStyle(rotation, tapePosition)} />

        {/* Miniatura */}
        <div
          style={{
            width: '100%',
            aspectRatio: '3/4',
            overflow: 'hidden',
            position: 'relative',
            backgroundColor: '#111',
            borderRadius: '1px',
          }}
        >
          <img
            src={poster}
            alt={title}
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              transform: hovered ? 'scale(1.05)' : 'scale(1)',
              transition: 'transform 0.45s ease',
              filter: hovered ? 'brightness(0.78)' : 'brightness(1)',
            }}
          />

          {/* Prévia muda em loop, aparece por cima do poster no hover */}
          {preview && (
            <video
              ref={previewRef}
              src={preview}
              muted
              loop
              playsInline
              preload="none"
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: hovered ? 1 : 0,
                transition: 'opacity 0.4s ease',
                pointerEvents: 'none',
              }}
            />
          )}

          {/* Botão de play */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: hovered ? 1 : 0.75,
              transition: 'opacity 0.3s ease',
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.93)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transform: hovered ? 'scale(1.12)' : 'scale(1)',
                transition: 'transform 0.3s ease',
                boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
              }}
            >
              <Play size={18} fill="#1a1a1a" color="#1a1a1a" style={{ marginLeft: '3px' }} />
            </div>
          </div>

          {/* Duração */}
          {duration && (
            <div
              style={{
                position: 'absolute',
                bottom: '8px',
                right: '8px',
                background: 'rgba(0,0,0,0.62)',
                color: '#f7f5ef',
                fontSize: '9.5px',
                padding: '2px 8px',
                borderRadius: '2px',
                fontFamily: '"Nunito", sans-serif',
                letterSpacing: '0.12em',
              }}
            >
              {fmtDuracao(duration)}
            </div>
          )}
        </div>

        {/* Texto do card */}
        <div style={{ padding: '14px 6px 0 6px' }}>
          <p
            style={{
              fontFamily: '"Nunito", sans-serif',
              fontSize: '8.5px',
              letterSpacing: '0.24em',
              color: '#a09080',
              margin: '0 0 4px 0',
              textTransform: 'uppercase',
            }}
          >
            {client}
          </p>
          <h3
            style={{
              fontFamily: '"Cormorant Garamond", "Georgia", serif',
              fontSize: '1.05rem',
              color: '#1a1a1a',
              margin: '0 0 5px 0',
              fontWeight: 600,
              lineHeight: 1.3,
            }}
          >
            {title}
          </h3>
          {role && (
            <p
              style={{
                fontFamily: '"Nunito", sans-serif',
                fontSize: '11px',
                color: '#888',
                margin: '0 0 8px 0',
                fontStyle: 'italic',
                lineHeight: 1.5,
              }}
            >
              {role}
            </p>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                style={{
                  fontFamily: '"Nunito", sans-serif',
                  fontSize: '8.5px',
                  padding: '2px 8px',
                  background: '#f0ede6',
                  color: '#7a6a5a',
                  borderRadius: '2px',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Modal do vídeo */}
      {modalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.92)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'clamp(0.8rem, 3vw, 2rem)',
          }}
          onClick={() => setModalOpen(false)}
        >
          <div
            style={{
              background: '#fff',
              padding: '10px 10px 18px 10px',
              maxWidth: orientation === 'vertical' ? '460px' : '900px',
              width: '100%',
              boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
              position: 'relative',
              borderRadius: '2px',
              maxHeight: '96vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setModalOpen(false)}
              aria-label="Fechar"
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'rgba(255,255,255,0.85)',
                border: 'none',
                cursor: 'pointer',
                color: '#444',
                lineHeight: 1,
                zIndex: 2,
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <X size={16} />
            </button>

            <div
              style={{
                aspectRatio: modalAspect,
                maxHeight: '72vh',
                margin: '0 auto',
                position: 'relative',
                background: '#111',
                overflow: 'hidden',
              }}
            >
              <video
                src={video}
                poster={poster}
                controls
                autoPlay
                playsInline
                preload="metadata"
                style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
            </div>

            <div style={{ padding: '14px 8px 0' }}>
              <p
                style={{
                  fontFamily: '"Nunito", sans-serif',
                  fontSize: '9px',
                  letterSpacing: '0.22em',
                  color: '#a09080',
                  margin: '0 0 4px',
                  textTransform: 'uppercase',
                }}
              >
                {client}
                {year ? ` · ${year}` : ''}
              </p>
              <h3
                style={{
                  fontFamily: '"Cormorant Garamond", "Georgia", serif',
                  fontSize: '1.25rem',
                  color: '#1a1a1a',
                  margin: '0 0 6px',
                  fontWeight: 600,
                }}
              >
                {title}
              </h3>
              {description && (
                <p
                  style={{
                    fontFamily: '"Nunito", sans-serif',
                    fontSize: '13px',
                    color: '#555',
                    lineHeight: 1.7,
                    margin: '0 0 10px',
                  }}
                >
                  {description}
                </p>
              )}
              {credits && (
                <p
                  style={{
                    fontFamily: '"Nunito", sans-serif',
                    fontSize: '10.5px',
                    color: '#8a7560',
                    letterSpacing: '0.04em',
                    lineHeight: 1.6,
                    margin: 0,
                    paddingTop: '10px',
                    borderTop: '1px solid rgba(0,0,0,0.07)',
                  }}
                >
                  {credits}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProjectCard;
